import { FILTER_SEPARATOR } from './Consts'
import type { Payment } from './tables/Payment'
import type { PejerTableNames, PejerTablesList } from './types/base'
import type { Filters, OperatorObject, OperatorObjectValue, SearchQuery } from './types/types'

// TODO Are these good messages??
export function paymentStatusToInformationString(status: Payment.PaymentStatus): string {
  switch (status) {
    case 'InitiatedPayment':
      return 'The payment has been initiated. Waiting for bank to confirm the payment'
    case 'ReservedPayment':
      return 'The payment has been reserved'
    case 'ChargedPayment':
      return 'The customer has been charged and the payment has been added to your balance'
    case 'RefundedPayment':
      return 'The payment has been refunded to the customer'
    case 'CancelledPayment':
      return 'The payment has been cancelled'
    case 'RefundInitiated':
      return 'A refund has been requested, and the money will be refunded shortly'
    case 'FailedPayment':
      return 'Payment has failed'
  }
  return 'Unknown status'
}
export function paymentStatusToString(status: Payment.PaymentStatus): string {
  switch (status) {
    case 'InitiatedPayment':
      return 'Initiated'
    case 'ReservedPayment':
      return 'Reserved'
    case 'ChargedPayment':
      return 'Charged'
    case 'RefundedPayment':
      return 'Refunded'
    case 'CancelledPayment':
      return 'Cancelled'
    case 'RefundInitiated':
      return 'Refund initiated'
    case 'FailedPayment':
      return 'Failed'
  }

  return 'Unknown status'
}

export function getColumnByStringIndex<T extends PejerTableNames>(
  obj: PejerTablesList<T>,
  column: keyof PejerTablesList<T> | string
): string {
  let col: any = obj
  for (const key of (column as string).split('.')) {
    col = col[key]
  }

  return col
}

export function getRecommendedGroupByTime(startDate: Date, endDate: Date) {
  const diff = endDate.getTime() - startDate.getTime()

  // if less than 2 hours
  if (diff < 1000 * 60 * 60 * 2) return 'Minute'

  // if less than 2 days
  if (diff < 1000 * 60 * 60 * 24 * 2) return 'Hour'

  // if less than 3 month
  if (diff < 1000 * 60 * 60 * 24 * 30 * 3) return 'Day'
  return 'Month'
}

export function stringifySearchArgs<T>(input: SearchQuery<T>) {
  const resultParts: string[] = []

  Object.entries(input).forEach(([key, valueObj]) => {
    Object.entries(valueObj).forEach(([operator, opObject]) => {
      const baseValue = `${key}${operator}${(opObject as any).value}`

      // Handle special properties like smartSearchEnabled and smartSearchValue
      const extraDetails = Object.entries(opObject)
        .filter(([detailKey]) => detailKey !== 'value')
        .map(([detailKey, detailValue]) => {
          if (detailValue === undefined) return ''

          if (detailKey in aliasMap) {
            return `${aliasMap[detailKey]}=${detailValue}`
          }
          return `${detailKey}=${detailValue}`
        })
        .join(',')

      const resultPart = extraDetails ? `${baseValue},${extraDetails}` : baseValue
      resultParts.push(resultPart)
    })
  })

  return resultParts.join(FILTER_SEPARATOR)
}

const aliasMap: Record<string, string> = {
  smartSearchEnabled: 'se',
  smartSearchValue: 'sv',
}

// Example usage:
// formatAbbreviation("BA") // Output "B.A"
// formatAbbreviation("IO") // Output "I.O"
// formatAbbreviation("H2H") // Output "H2H"
function formatAbbreviation(abbreviation: string): string {
  // Check if the abbreviation contains numbers
  if (/\d/.test(abbreviation)) {
    // If there are digits, return the string unchanged
    return abbreviation
  } else {
    // If no digits, insert dots between the letters
    return abbreviation.split('').join('.')
  }
}

// Example usage:
// "Bloc As" => B.A
// "IdrettenOnline" => I.O
// "Hai2Hai" => H2H
// "Data4You" => D4Y
export function abbreviate(text: string): string {
  // This pattern matches each uppercase letter followed by lowercase (as new words),
  // or digits, or isolated uppercase letters (considering each as a significant part for abbreviation).
  const parts = text.match(/[A-Z][a-z]*|\d+/g) || []

  // Reduce the parts to form an abbreviation: Take the first character of each match and ensure it's uppercase,
  // except for numbers, which are used as is.
  const abbreviation = parts
    .map((part) => (isNaN(parseInt(part)) ? part[0].toUpperCase() : part))
    .join('')

  return formatAbbreviation(abbreviation)
}

export function generateUUIDv4() {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16)
  )
}

export function parseSearchArgsFromString<T>(input: string): SearchQuery<T> {
  const result = {} as any

  // Split the input string by ";" to get each condition
  const conditions = input.split(FILTER_SEPARATOR)

  conditions.forEach((condition) => {
    // First, separate the condition from any additional parameters
    const [baseCondition, ...additionalParams] = condition.split(',')

    // Next, extract the field name, operator, and value.
    // This regex now accounts for operators like '!=', '>=', '<=', etc.
    const match = baseCondition.match(/([^!<>=]+)(!?[<>=]+)(.+)/)

    if (!match) return // Skip if the format does not match expected pattern

    const [, field, operator, value] = match

    // Initialize the structure for this field in the result object
    const detail: any = { value }

    // Process any additional parameters
    additionalParams.forEach((param) => {
      const [key, val] = param.split('=')

      if (key === aliasMap['smartSearchEnabled']) {
        // Convert "true" and "false" strings to boolean values
        detail.smartSearchEnabled = val === 'true'
      } else if (key === aliasMap['smartSearchValue']) {
        detail.smartSearchValue = val
      } else {
        detail[key] = val
      }
    })

    // Assign the structured detail to the corresponding field and operator in the result
    const op = operator || '=' // Default to "=" if operator is missing
    result[field] = { ...result[field], [op]: detail }
  })

  return result
}

export function saveVisibleDataTableColumns(id: string, data: Record<string, boolean>) {
  if (Object.keys(data).length === 0) localStorage.removeItem('dataTableColumnsVisible:' + id)
  else localStorage.setItem('dataTableColumnsVisible:' + id, JSON.stringify(data))
}

export function getSavedDataTableVisibleColumns(id: string): Record<string, boolean> {
  const storedData = localStorage.getItem('dataTableColumnsVisible:' + id)
  return storedData ? JSON.parse(storedData) : {}
}

export function getQueryStringFromFiltersObject<T>(query: Filters<T>): string {
  const createQueryString = (filters: SearchQuery<T>): string =>
    Object.entries(filters)
      .map((entries) => {
        const [column, operatorObjectOrValue] = entries as [
          keyof SearchQuery<T>,
          OperatorObject | OperatorObjectValue | SearchQuery<T>[],
        ]
        if (operatorObjectOrValue === undefined) return ''

        // If the operatorObject is an array, we know its a SearchQuery
        if (Array.isArray(operatorObjectOrValue)) {
          if (operatorObjectOrValue.length === 0) return ''
          if (column !== 'AND' && column !== 'OR')
            throw new Error(`Invalid column name for array: ${column}`)
          return operatorObjectOrValue
            .map((value) => `(${createQueryString(value)})`)
            .filter((x) => x)
            .join(column === 'AND' ? ' && ' : ' || ')
        }

        // Then we check if the operatorList is a primitive
        if (typeof operatorObjectOrValue !== 'object' || operatorObjectOrValue === null)
          return `${column}=${operatorObjectOrValue}`

        // If we reach here, we know the operatorObject is an object with operators as keys
        return Object.entries(operatorObjectOrValue)
          .map(([operator, value]) => {
            // const smartSearchEnabled =
            //   'smartSearchValue' in value && value.smartSearchEnabled && value.smartSearchValue
            // const rawValue = (smartSearchEnabled ? value.smartSearchValue : value.value) as string

            // if (rawValue && typeof value.value === 'string') {
            //   return rawValue
            //     .split('|')
            //     .map((x) => {
            //       if (x == 'null') return x
            //       if (smartSearchEnabled) return `"%${x}%"`
            //       return `"${x}"`
            //     })
            //     .join('|')
            // }

            if (value === undefined) return ''

            // The value may be a primitive or an object with a value property.
            const normalizedValue =
              typeof value === 'object' && value !== null ? value.value : value
            return `${column}${operator}${normalizedValue}`
          })
          .filter((x) => x)
          .join(FILTER_SEPARATOR)
      })
      .filter((x) => x)
      .join(FILTER_SEPARATOR)

  const queryStrings = []
  if (query.staticFilters) queryStrings.push(createQueryString(query.staticFilters))
  if (query.userFilters) queryStrings.push(createQueryString(query.userFilters))

  return queryStrings
    .filter((x) => x)
    .map((x) => `(${x})`)
    .join(FILTER_SEPARATOR)
}
